import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  Circle,
  ChevronDown,
  ChevronUp,
  X,
  Rocket,
  Settings,
  Code2,
  BookOpen,
  GitBranch,
  Users,
  ArrowRight,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useSetupProgress, type SetupStep } from '../../hooks/useSetupProgress';

const STEP_ICONS: Record<string, React.ElementType> = {
  'general-settings': Settings,
  'install-widget': Code2,
  'add-kb-article': BookOpen,
  'create-flow': GitBranch,
  'invite-team': Users,
};

const DISMISSED_KEY = 'sg_setup_checklist_dismissed';

function StepRow({ step, index }: { step: SetupStep; index: number }) {
  const Icon = STEP_ICONS[step.id] ?? Circle;
  return (
    <Link
      to={step.complete ? '#' : step.href}
      onClick={(e) => step.complete && e.preventDefault()}
      className={cn(
        'flex items-center gap-3 px-4 py-3 rounded-xl border transition-all group',
        step.complete
          ? 'border-accent/20 bg-accent/5 cursor-default'
          : 'border-border bg-surface hover:border-accent/40 hover:bg-accent/5 cursor-pointer',
      )}
    >
      {/* Step number / check */}
      <div
        className={cn(
          'w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold transition-colors',
          step.complete
            ? 'bg-accent/15 text-accent'
            : 'bg-surface-2 text-text-3 group-hover:bg-accent/10 group-hover:text-accent',
        )}
      >
        {step.complete ? (
          <CheckCircle2 className="w-4 h-4" />
        ) : (
          <span>{index + 1}</span>
        )}
      </div>

      {/* Icon */}
      <div
        className={cn(
          'w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0',
          step.complete ? 'bg-accent/10 text-accent' : 'bg-surface-2 text-text-3 group-hover:text-accent',
        )}
      >
        <Icon className="w-3.5 h-3.5" />
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            'text-sm font-medium leading-snug',
            step.complete ? 'text-text-2 line-through decoration-text-3' : 'text-text',
          )}
        >
          {step.label}
        </p>
        {!step.complete && (
          <p className="text-xs text-text-3 mt-0.5 leading-snug truncate">{step.description}</p>
        )}
      </div>

      {/* Arrow (incomplete only) */}
      {!step.complete && (
        <ArrowRight className="w-4 h-4 text-text-3 flex-shrink-0 group-hover:text-accent group-hover:translate-x-0.5 transition-all" />
      )}
    </Link>
  );
}

export default function SetupChecklist() {
  const { data, isLoading } = useSetupProgress();
  const [collapsed, setCollapsed] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  function dismiss() {
    try {
      localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // ignore
    }
    setDismissed(true);
  }

  // Don't render while loading, if dismissed, or if all steps complete and user closed it
  if (isLoading || dismissed) return null;
  if (!data) return null;

  // Auto-hide once everything's done (but show a brief "all done" celebration)
  // We still show it until explicitly dismissed when all complete
  const { steps, completedCount, totalCount, percentComplete, allComplete } = data;

  return (
    <div
      className={cn(
        'border-b border-border bg-surface transition-all',
        allComplete && 'bg-accent/5 border-accent/20',
      )}
    >
      {/* Header row */}
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Rocket icon */}
        <div
          className={cn(
            'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
            allComplete ? 'bg-accent/15 text-accent' : 'bg-accent/10 text-accent',
          )}
        >
          <Rocket className="w-4 h-4" />
        </div>

        {/* Title + progress */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-text">
              {allComplete ? 'Setup complete!' : 'Get started with ServeGrace'}
            </p>
            <span className="text-xs text-text-3 font-medium">
              {completedCount}/{totalCount} steps
            </span>
          </div>

          {/* Progress bar */}
          <div className="mt-1.5 h-1.5 bg-surface-2 rounded-full overflow-hidden w-48">
            <div
              className={cn(
                'h-full rounded-full transition-all duration-500',
                allComplete ? 'bg-accent' : 'bg-accent',
              )}
              style={{ width: `${percentComplete}%` }}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {allComplete ? (
            <button
              onClick={dismiss}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-accent hover:bg-accent-hover rounded-lg transition-colors"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              Dismiss
            </button>
          ) : (
            <button
              onClick={() => setCollapsed((c) => !c)}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-text-2 hover:text-text hover:bg-surface-2 rounded-lg transition-colors"
            >
              {collapsed ? (
                <>
                  <ChevronDown className="w-3.5 h-3.5" />
                  Show
                </>
              ) : (
                <>
                  <ChevronUp className="w-3.5 h-3.5" />
                  Hide
                </>
              )}
            </button>
          )}
          <button
            onClick={dismiss}
            className="w-7 h-7 flex items-center justify-center text-text-3 hover:text-text hover:bg-surface-2 rounded-lg transition-colors"
            title="Dismiss setup checklist"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Steps grid (collapsible) */}
      {!collapsed && !allComplete && (
        <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2">
          {steps.map((step, i) => (
            <StepRow key={step.id} step={step} index={i} />
          ))}
        </div>
      )}

      {/* All complete message */}
      {allComplete && (
        <div className="px-4 pb-3">
          <p className="text-xs text-text-2">
            You're all set! ServeGrace is fully configured and ready to support your customers.
          </p>
        </div>
      )}
    </div>
  );
}
